import { customerKeys } from '../components/customers/customerKeys';
import { format } from 'date-fns';
import { Customer } from 'types/customer';

/**
 * Mérési típus érték formázása emberileg olvasható címkére
 * @param value A formázandó érték (negyedoras, profil, stb.)
 * @returns Emberileg olvasható címke
 */
const formatMeasurementType = (value: string): string => {
  const mapping: Record<string, string> = {
    'negyedoras': 'AMR - Negyedórás (15 perc)',
    'negyedoras_hmke': 'AMR - Negyedórás (15 perc) - HMKE',
    'profil': 'SLP (profil alapú)',
    'tavmert_slp': 'SLP (KIF4) - Távmért',
    'profil_hmke': 'SLP (profil alapú) - HMKE',
    'tavmert_slp_hmke': 'SLP (KIF4) - Távmért - HMKE',
    'AMR': 'AMR - Negyedórás (15 perc)',
    'SLP': 'SLP (profil alapú)'
  };
  return mapping[value] || value || '';
};

/**
 * Fogyasztás mező formázása ezres tagolással
 * @param value A formázandó érték
 * @returns Formázott érték ezres tagolással
 */
const formatConsumption = (value: string | number): string => {
  if (!value && value !== 0) return '';
  
  // Konvertáljuk string-re ha number típusú
  const stringValue = typeof value === 'number' ? value.toString() : value;
  
  // Eltávolítjuk a nem-szám karaktereket, kivéve a tizedespontot
  const cleanValue = stringValue.replace(/[^0-9.]/g, '');
  
  // Szétválasztjuk a tizedes rész előtti és utáni részre
  const parts = cleanValue.split('.');
  
  // Formázzuk az egész részt ezres tagolással
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  
  // Összerakjuk az eredményt
  return parts.length > 1 ? parts.join('.') : parts[0];
};

/**
 * Az ügyfél adatokat a dokumentumgeneráláshoz használható kulcs-érték párokká alakítja
 * 
 * @param customer Az ügyfél objektum
 * @returns A kulcs-érték párok objektuma, ami a dokumentumgeneráláshoz használható
 */
export const customerToKeyValuePairs = (customer: Customer): Record<string, string> => {
  // Lemásoljuk a sablont, hogy ne módosítsuk az eredetit
  const mappedData = { ...customerKeys } as Record<string, string>;

  if (!customer) return mappedData;

  // Alapadatok
  if (customer.type === 'organization') {
    mappedData['Cég megnevezése'] = customer.companyName || '';
  } else {
    mappedData['Cég megnevezése'] = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
  }

  // Szerződésszám
  if ('contractNumber' in customer && customer.contractNumber) {
    mappedData['Szerződés száma'] = customer.contractNumber;
  }

  if ('companyAddress' in customer && customer.companyAddress) {
    mappedData['Cég címe'] = customer.companyAddress;
  }

  // Adószám és cégjegyzékszám
  mappedData['Cég adószáma'] = customer.taxNumber || '';

  if ('registrationNumber' in customer && customer.registrationNumber) {
    mappedData['Cégjegyzékszám'] = customer.registrationNumber;
  }

  // Bankszámlaszám
  if ('bankAccount' in customer && customer.bankAccount) {
    mappedData['Bankszámlaszáma'] = customer.bankAccount;
  }

  // Képviselő neve
  if ('representativeName' in customer && customer.representativeName) {
    mappedData['Képviselő neve'] = customer.representativeName;
  }

  // Számlázási e-mail
  if ('billingEmail' in customer && customer.billingEmail) {
    mappedData['Számlázási e-mail'] = customer.billingEmail;
  }

  // További számlázási e-mail címek
  if ('additionalBillingEmails' in customer && customer.additionalBillingEmails && customer.additionalBillingEmails.length > 0) {
    mappedData['További számlázási e-mail címek'] = customer.additionalBillingEmails.join(', ');
  }

  // Kapcsolattartó adatok
  if (customer.contactPerson) {
    mappedData['Kapcsolattartó neve'] = customer.contactPerson.name || '';
    mappedData['Kapcsolattartó e-mail'] = customer.contactPerson.email || '';
    mappedData['Kapcsolattartó telefon'] = customer.contactPerson.phone || '';

    if ('fax' in customer.contactPerson && customer.contactPerson.fax) {
      mappedData['Kapcsolattartó fax'] = customer.contactPerson.fax;
    }
  }

  // Pénzügyi kapcsolattartó
  if ('financialContact' in customer && customer.financialContact) {
    mappedData['Pénzügyi kapcsolattartó neve'] = customer.financialContact.name || '';
    mappedData['Pénzügyi kapcsolattartó e-mail'] = customer.financialContact.email || '';
    mappedData['Pénzügyi kapcsolattartó telefon'] = customer.financialContact.phone || '';
  }

  // Fogyasztási adatok
  if ('consumptionData' in customer && customer.consumptionData) {
    mappedData['Dátum'] = customer.consumptionData.date || '';
    
    // Fizetési határidő napban, hozzáadjuk a "nap" szöveget
    if (customer.consumptionData.dueDate) {
      mappedData['Fizetési határidő'] = `${customer.consumptionData.dueDate} nap`;
    } else {
      mappedData['Fizetési határidő'] = '';
    }
    
    mappedData['Teljesítmény (kW)'] = customer.consumptionData.power || '';
    mappedData['Profil- szám'] = customer.consumptionData.profileNumber || '';
    mappedData['Fogyasztás kWh'] = formatConsumption(customer.consumptionData.consumption) || '';
  }

  // POD adatok
  if ('pod' in customer && customer.pod) {
    mappedData['POD'] = customer.pod.id || '';
    mappedData['Fogy.hely cím'] = customer.pod.address || '';
    mappedData['Irányítószám'] = customer.pod.postalCode || '';
    mappedData['Város'] = customer.pod.city || '';
    mappedData['Utca, házszám'] = customer.pod.street || '';
    mappedData['Távmért / Profilos'] = formatMeasurementType(customer.pod.measurementType) || '';
    mappedData['MÉF / Éves mennyiség (kWh)'] = formatConsumption(customer.pod.annualConsumption) || '';
  }

  // További POD adatok
  if ('additionalPods' in customer && customer.additionalPods && customer.additionalPods.length > 0) {
    for (let i = 0; i < customer.additionalPods.length; i++) {
      const podNumber = i + 2; // POD 2-től kezdve
      const pod = customer.additionalPods[i];

      mappedData[`POD ${podNumber}`] = pod.id || '';
      mappedData[`Fogy.hely cím ${podNumber}`] = pod.address || '';
      mappedData[`Irányítószám ${podNumber}`] = pod.postalCode || '';
      mappedData[`Város ${podNumber}`] = pod.city || '';
      mappedData[`Utca, házszám ${podNumber}`] = pod.street || '';
      mappedData[`Távmért / Profilos ${podNumber}`] = formatMeasurementType(pod.measurementType) || '';
      mappedData[`MÉF ${podNumber} / Éves mennyiség (kWh)`] = formatConsumption(pod.annualConsumption) || '';
    }
  }

  // Dinamikus POD táblázat generálása
  const podTable = generatePodTable(customer);
  mappedData['POD_TABLE'] = podTable;
  console.log('POD táblázat generálva:', podTable ? `${podTable.length} karakter` : 'üres');

  // Szerződés időszak
  if ('contractPeriod' in customer && customer.contractPeriod) {
    mappedData['Szerződés kezdete'] = customer.contractPeriod.startDate || '';
    mappedData['Szerződés vége'] = customer.contractPeriod.endDate || '';
    mappedData['Szerződéses ár'] = customer.contractPeriod.price ? formatConsumption(customer.contractPeriod.price) : '';
  }

  // Ajánlati ár és valutanem - először currentPricing-ból, utána offerPrice-ból
  let currency = '';
  
  // Először a currentPricing.currency-t nézzük (ez az aktuális ár)
  if ('currentPricing' in customer && customer.currentPricing && customer.currentPricing.currency) {
    currency = customer.currentPricing.currency;
  }
  // Ha nincs currentPricing, akkor az offerPrice.currency-t használjuk
  else if ('offerPrice' in customer && customer.offerPrice && customer.offerPrice.currency) {
    currency = customer.offerPrice.currency;
  }
  
  // Beállítjuk az ár egységet a valuta alapján
  if (currency === 'HUF') {
    mappedData['Ajánlati ár HUF/kWh-EUR/MWh'] = 'HUF/kWh';
    mappedData['Ár egység'] = 'HUF/kWh';
    mappedData['Valutanem'] = 'HUF';
  } else if (currency === 'EUR') {
    mappedData['Ajánlati ár HUF/kWh-EUR/MWh'] = 'EUR/MWh';
    mappedData['Ár egység'] = 'EUR/MWh';
    mappedData['Valutanem'] = 'EUR';
  }
  
  // Ha van offerPrice, akkor azt is beállítjuk (fallback)
  if ('offerPrice' in customer && customer.offerPrice && customer.offerPrice.currency) {
    mappedData['Valutanem'] = customer.offerPrice.currency;
  }

  // KÁT adatok
  if ('kat' in customer && customer.kat) {
    mappedData['KÁT'] = customer.kat.kat || '';
    mappedData['KÁT szöveg'] = customer.kat.text || '';
  }

  // Aktuális dátum, ha nincs még beállítva
  if (!mappedData['Dátum']) {
    mappedData['Dátum'] = format(new Date(), 'yyyy.MM.dd.');
  }

  return mappedData;
};

/**
 * Dinamikus POD táblázatot generál az összes POD-ból
 * 
 * @param customer Az ügyfél objektum
 * @returns Word dokumenetumban használható HTML-szerű táblázat
 */
const generatePodTable = (customer: Customer): string => {
  if (!customer) return '';

  const allPods = [];
  
  // Fő POD
  if ('pod' in customer && customer.pod) {
    allPods.push(customer.pod);
  }
  
  // További POD-ok
  if ('additionalPods' in customer && customer.additionalPods && customer.additionalPods.length > 0) {
    allPods.push(...customer.additionalPods);
  }

  if (allPods.length === 0) return '';

  // Word XML táblázat fejléc
  let tableXml = `
    <w:tbl>
      <w:tblPr>
        <w:tblStyle w:val="TableGrid"/>
        <w:tblW w:w="0" w:type="auto"/>
        <w:tblLook w:val="04A0" w:firstRow="1" w:lastRow="0" w:firstColumn="1" w:lastColumn="0" w:noHBand="0" w:noVBand="1"/>
        <w:tblBorders>
          <w:top w:val="single" w:sz="4" w:space="0" w:color="auto"/>
          <w:left w:val="single" w:sz="4" w:space="0" w:color="auto"/>
          <w:bottom w:val="single" w:sz="4" w:space="0" w:color="auto"/>
          <w:right w:val="single" w:sz="4" w:space="0" w:color="auto"/>
          <w:insideH w:val="single" w:sz="4" w:space="0" w:color="auto"/>
          <w:insideV w:val="single" w:sz="4" w:space="0" w:color="auto"/>
        </w:tblBorders>
      </w:tblPr>
      <w:tblGrid>
        <w:gridCol w:w="2340"/>
        <w:gridCol w:w="2340"/>
        <w:gridCol w:w="2340"/>
        <w:gridCol w:w="2340"/>
      </w:tblGrid>
      <!-- Table header -->
      <w:tr>
        <w:trPr>
          <w:tblHeader/>
        </w:trPr>
        <w:tc>
          <w:tcPr><w:shd w:fill="DDDDDD" w:val="clear"/></w:tcPr>
          <w:p><w:r><w:rPr><w:b/><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>POD</w:t></w:r></w:p>
        </w:tc>
        <w:tc>
          <w:tcPr><w:shd w:fill="DDDDDD" w:val="clear"/></w:tcPr>
          <w:p><w:r><w:rPr><w:b/><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>Fogyasztási hely címe</w:t></w:r></w:p>
        </w:tc>
        <w:tc>
          <w:tcPr><w:shd w:fill="DDDDDD" w:val="clear"/></w:tcPr>
          <w:p><w:r><w:rPr><w:b/><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>Mérési mód</w:t></w:r></w:p>
        </w:tc>
        <w:tc>
          <w:tcPr><w:shd w:fill="DDDDDD" w:val="clear"/></w:tcPr>
          <w:p><w:r><w:rPr><w:b/><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>Éves fogyasztás (kWh)</w:t></w:r></w:p>
        </w:tc>
      </w:tr>`;

  // Táblázat sorok
  allPods.forEach(pod => {
    // Ellenőrizzük, hogy mi a POD és mi a cím
    // Ha a pod.id olyan formátumú, mint egy cím (nem tartalmaz elég számot) és 
    // a pod.address viszont HU-val kezdődő POD azonosító, akkor felcserélődtek
    const isPodSwapped = 
      pod.id && pod.address && 
      (
        (pod.id.includes(' ') && pod.address.includes('HU')) || 
        (!pod.id.includes('-') && pod.address && pod.address.includes('-'))
      );
    
    const podId = isPodSwapped ? pod.address : pod.id;
    const podAddress = isPodSwapped ? pod.id : pod.address;
    
    tableXml += `
      <w:tr>
        <w:tc>
          <w:p><w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>${podId || ''}</w:t></w:r></w:p>
        </w:tc>
        <w:tc>
          <w:p><w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>${podAddress || ''}</w:t></w:r></w:p>
        </w:tc>
        <w:tc>
          <w:p><w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>${formatMeasurementType(pod.measurementType) || ''}</w:t></w:r></w:p>
        </w:tc>
        <w:tc>
          <w:p><w:r><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr><w:t>${formatConsumption(pod.annualConsumption) || ''}</w:t></w:r></w:p>
        </w:tc>
      </w:tr>`;
  });

  // Táblázat lezárása
  tableXml += `</w:tbl>`;

  return tableXml;
}; 